import { DesignSystem } from '@/src/design-system'
import { Image } from 'expo-image'
import { Paragraph, Separator, useEvent, YStack } from 'tamagui'
import { CompetitiveRates } from '@/src/experiences/home/assets/CompetitiveRates'
import { ExpertGuidance } from '@/src/experiences/home/assets/ExpertGuidance'
import { PointsOnLoan } from '@/src/experiences/home/assets/PointsOnLoan'
import { Benefit } from './Benefit'
import { mesaColorNames, typography } from '@/src/common/theme'
import { BenefitsSkeleton } from './BenefitsSkeleton'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { Button } from '@/src/common/components/Button'
import * as E2E from '../../e2e'
import * as Hooks from '../../hooks'
import * as Statsig from '../../statsig'
import * as Stores from '../../stores'
import { useStateCode } from '@/src/common/hooks/useMortgageModuleGeoIPBehavior'
import { useSegmentDomain } from '@/src/services/segment/SegmentDomainProvider'

export { BenefitsSkeleton }

export function BenefitsCard() {
  const stateCode = useStateCode()
  const stateConfig = Hooks.useStateConfig(stateCode)
  const moduleDesign = Statsig.Store.useMortgageModuleDesign('default')
  const { triggerAction } = useSegmentDomain()
  const { data: scheduledCallData } = Stores.MortgageApi.endpoints[
    'GET /mortgage/leads/scheduled-calls'
  ].useQuery({})

  const handleGetStarted = useEvent(async () => {
    if (moduleDesign.action_url) {
      void triggerAction('BenefitsCard', 'GetStarted')
      await openInAppBrowser(moduleDesign.action_url, moduleDesign.action_text)
    }
  })

  if (stateConfig === 'loading') {
    return <BenefitsSkeleton />
  }

  // Show the CTA if:
  // - The module is enabled
  // - Scheduled Call data has resolved
  // - There is a scheduled call -or- the schedule call feature is disabled
  // Otherwise, hide the CTA so the Schedule Call CTA takes priority re: UX
  const showCTA =
    stateConfig.enableInAppLeadForm &&
    scheduledCallData &&
    (scheduledCallData.hasCallScheduled || !stateConfig.enableScheduleACall)

  return (
    <DesignSystem.SplitCard testID={E2E.Config.Ids.View.BenefitsCard}>
      <DesignSystem.SplitCard.ImageHeader containerStyle={{ height: 160 }}>
        <Image
          source={require('@/src/experiences/home/assets/mesa_mortgage_logo.png')}
          contentFit="contain"
          style={{ height: 120, width: '100%', marginVertical: 20 }}
        />
      </DesignSystem.SplitCard.ImageHeader>
      <DesignSystem.SplitCard.Content>
        <YStack gap="$4" px="$2" overflow="hidden">
          <Paragraph>
            Mesa Mortgage is revolutionizing homeownership by making your loan rewarding.
          </Paragraph>
          <YStack gap="$2" items="stretch">
            <Benefit
              title="Competitive Rates"
              description="Low, transparent rates to help you save more."
              icon={<CompetitiveRates width={24} height={24} />}
            />
            <Separator />
            <Benefit
              title="Expert Guidance"
              description="Dedicated experts make your mortgage simple."
              icon={<ExpertGuidance width={24} height={24} />}
            />
            <Separator />
            <Benefit
              title="Earn valuable Mesa Points"
              description="Earn 1X Mesa Points per $1 on the principal amount of your loan."
              icon={<PointsOnLoan width={24} height={24} />}
            />
          </YStack>
          {showCTA && (
            <Button
              title={moduleDesign.action_text}
              onPress={() => void handleGetStarted()}
              testID={E2E.Config.Ids.Press.GetStarted}
            />
          )}
          <DesignSystem.Markdown
            style={{
              body: {
                color: mesaColorNames['Greyscale/400 #A4AAAC'],
                ...typography.XS,
              },
              link: {
                ...typography.XS,
              },
            }}
          >
            Mesa Mortgage NMLS # 2685861. This is not a commitment to lend. Mesa Mortgage is not
            available in all states. [Disclosures and
            Licensing](https://mesamember.mortgage/legal/mesa-mortgage-disclosures-licensing/).
          </DesignSystem.Markdown>
        </YStack>
      </DesignSystem.SplitCard.Content>
    </DesignSystem.SplitCard>
  )
}
